import { Box } from '@material-ui/core';
import { DataGrid, GridColDef, GridColumns } from '@material-ui/data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getCfops } from '~/pages/DevolucaoESaidaNFDeFornecedor/services';
import { useEmissaoNFE } from '~/pages/EmissaoNFE/EmissaoNFEContext';
import { CalculosDaTabela } from '~/pages/EmissaoNFE/functions/Calculos';
import { moneyFormat } from '~/utils/functions';
import { defineCFOP } from '~/pages/DevolucaoESaidaNFDeFornecedor/functions/Auxiliares/DefineCFOP';

export const GridPedidoPendente: React.FC<{ pedidoGrid: any[] }> = ({
  pedidoGrid,
}) => {
  const [selectedRowId, setSelectedRowId] = useState<any[]>([]);
  const {
    loja,
    produtos,
    parceiro,
    pedidosPendentes,
    pedidoPendenteSelected,
    handleShowTablePedidosPendentes,
    setItensPedidoPendente,
    handlePedidoPendenteSelected,
    setPedidoPententeSelected,
    formNFE: { getValues: getFormNFEValues },
    pedido,
  } = useEmissaoNFE();
  const createColumn = useCallback(
    (
      field: string,
      headerName: string,
      options: Partial<GridColDef> = {},
    ): GridColDef => ({
      field,
      headerName,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      ...options,
    }),
    [],
  );

  function mapearItensParaCalculo(pedidos: any): any[] {
    return pedidos.map((item: any) => ({
      cod_ncm: item.cod_ncm,
      val_total_produto: item.val_total_produto,
      val_desconto: item.val_desconto || 0,
      val_acrescimo: item.val_acrescimo || 0,
      val_venda_pdv: item.val_pedido,
      qtd_total_produto: item.qtd_pedido,
      val_reducao: 0,
      per_fcp_icms: 0,
      flg_ignora_icms_nf_cli: false,
      flg_calcula_st: false,
      flg_prod_propria: item.flg_prod_propria,
      qtd_embalagem_venda: item.qtd_embalagem || 1,
      des_produto: item.des_produto,
      cod_produto: item.cod_produto,
      des_unidade: item.des_unidade,
      ...item,
    }));
  }

  const handleRowClick = async (params: any) => {
    const selectRowId = params.row.id;
    const { num_pedido, cod_loja } = params.row;

    if (verifyChecked(params)) {
      setSelectedRowId((prevSelected) =>
        prevSelected.filter((item) => item !== selectRowId),
      );
      setItensPedidoPendente((prevItens) => {
        const removeItensSelected = prevItens.filter(
          (item: any) => item.cod_seq_pedido !== selectRowId,
        );
        if (removeItensSelected.length === 0) {
          handleShowTablePedidosPendentes(true, false);
        }
        return removeItensSelected;
      });

      if (pedidoPendenteSelected.length > 0) {
        const pedidoRemove = pedidoPendenteSelected.filter(
          (item: any) => item.cod_seq_pedido !== selectRowId,
        );
        setPedidoPententeSelected(pedidoRemove);
      }
      return;
    }

    setSelectedRowId((prevSelected: any[] | null) => {
      const currentSelected = prevSelected ?? [];
      return [...currentSelected, selectRowId];
    });

    const pedidoPendente = pedidosPendentes.find(
      (p: any) =>
        Number(p.num_pedido) === Number(num_pedido) &&
        Number(p.cod_loja) === Number(cod_loja),
    );

    if (pedidoPendente) {
      const itensToCalculo = mapearItensParaCalculo(
        pedidoPendente.des_json_pedido_item,
      );

      const perfil = getFormNFEValues('cod_perfil');
      const cfops = await getCfops(perfil.cod_perfil);
      const { flg_orgao_publico } = getFormNFEValues('busca_parceiro');
      const itensWithCfop = itensToCalculo.map((i) => {
        const dataCFOP = defineCFOP(
          i.tipo_tributacao_ent_nf,
          i.flg_bonificado,
          loja.uf !== parceiro.des_uf,
          false,
          cfops,
        );
        if (dataCFOP) {
          i.cfop = dataCFOP.cfop;
          i.cfopData = dataCFOP;
        }
        return i;
      });

      const pedidoItens = await Promise.all(
        itensWithCfop.map(async (item, index) => {
          const formatedItems = await CalculosDaTabela({
            itens: [item],
            flg_orgao_publico,
            tipo_regime: loja.tipo_regime,
            des_sigla: loja.uf,
            cfop: item.cfop,
            lastIndex: produtos.length + index,
          });
          return formatedItems;
        }),
      );

      const pedidoItensFormatted = pedidoItens.flat().map((item: any) => {
        const tributacao = {
          cod_bc_mr: item.cod_bc_mr ?? 0,
          cod_situacao_tributaria: item.cod_situacao_tributaria,
          cod_tributacao: item.cod_tributacao,
          cod_tributacao_pdv: item.cod_tributacao_pdv ?? null,
          des_tributacao: item.des_tributacao,
          label: `${item.cod_tributacao} - ${item.des_tributacao}`,
          tipo_tributacao: item.tipo_tributacao,
          val_icms: item.val_icms,
          val_reducao: item.val_reducao,
          value: item.cod_tributacao,
        };
        return {
          id: item.cod_seq_pedido_item,
          cod_seq_pedido: selectRowId,
          tribData: tributacao,
          ...item,
          flg_bonificado: false,
          val_custo_rep_view: moneyFormat(item.val_custo_rep.toFixed(2)),
          val_total_produto_view: moneyFormat(
            item.val_total_produto.toFixed(2),
          ),
          val_pedido_view: moneyFormat(item.val_pedido.toFixed(2)),
        };
      });
      handleShowTablePedidosPendentes(true, true);
      setItensPedidoPendente((prevItens) => {
        const resultado = [...prevItens, ...pedidoItensFormatted];
        return resultado;
      });

      handlePedidoPendenteSelected(pedidoPendente);
    } else {
      toast.warning(`Nenhum item encontrado para o pedido: ${num_pedido}`);
    }
  };

  const renderActionCell = (params: { row: any }) => {
    return (
      <input
        type="checkbox"
        checked={
          selectedRowId?.includes(params.row.id) || verifyChecked(params)
        }
        readOnly
      />
    );
  };

  const columns: GridColumns = [
    createColumn('id', 'Id', {
      hide: true,
      width: 100,
      disableColumnMenu: true,
    }),
    createColumn('', 'Sel?', {
      cellClassName: 'super-app-theme--cell',
      headerClassName: 'super-app-theme--header',
      disableColumnMenu: true,
      width: 70,
      renderCell: renderActionCell,
    }),
    createColumn('num_pedido', 'Núm. Pedido', {
      width: 115,
      disableColumnMenu: true,
      headerAlign: 'left',
      align: 'left',
    }),
    createColumn('qtd_pedido_item', 'Qtd Itens', {
      width: 95,
      disableColumnMenu: true,
      headerAlign: 'left',
      align: 'left',
    }),
    createColumn('nome_pessoa', 'Parceiro', {
      width: 250,
      disableColumnMenu: true,
      headerAlign: 'left',
      align: 'left',
    }),
    createColumn('val_pedido', 'Valor', {
      width: 100,
      disableColumnMenu: true,
      headerAlign: 'right',
      align: 'right',
    }),
    createColumn('dta_emissao', 'Emissão', {
      width: 100,
      disableColumnMenu: true,
    }),
  ];
  const verifyChecked = (params: any): boolean => {
    return (
      pedido.some((item: any) => item.num_pedido === params.row.num_pedido) ||
      selectedRowId?.some((element) => element === params.row.id)
    );
  };
  return (
    <Box sx={{ height: 230, width: '100%' }}>
      <DataGrid
        rows={pedidoGrid}
        columns={columns}
        disableColumnFilter
        disableColumnSelector
        disableSelectionOnClick
        disableColumnMenu
        hideFooterPagination
        hideFooterRowCount
        hideFooterSelectedRowCount
        hideFooter
        onRowClick={handleRowClick}
        getRowClassName={(params) =>
          selectedRowId?.includes(params.row.id) || verifyChecked(params)
            ? 'selected-row'
            : ''
        }
        localeText={{
          noRowsLabel: 'Nenhum pedido encontrado',
          columnMenuLabel: 'Menu',
          columnMenuFilter: 'Filtrar',
          columnMenuHideColumn: 'Esconder',
          columnMenuUnsort: 'Não ordenar',
          columnMenuSortAsc: 'Ordernar ASC',
          columnMenuSortDesc: 'Ordernar DESC',
          columnMenuShowColumns: 'Mostrar colunas',
        }}
      />
      <style>{`
        .selected-row {
          background-color: #B3DAB6 !important;
        }
      `}</style>
    </Box>
  );
};
